import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchTrends,
  type DashboardTrends,
  type TrendOccupancyPoint,
  type TrendWeek,
  type BackendClientId,
} from '../services/dashboardApi';

export interface TrendChartsTheme {
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  sectionTitle: string;
  sectionDivider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  /** Primary accent — used for the leads / occupancy lines */
  accent: string;
  /** Secondary highlight — tours line */
  highlight: string;
  /** Negative tone — FU rate line (lower is better, but drawn with same emphasis) */
  negative: string;
}

interface TrendChartsProps {
  backendClientId: BackendClientId;
  /** Optional portfolio key (e.g. 'dwa-managed') to scope all series. */
  portfolio?: string;
  /** Optional NameUnique to scope all series to a single community. */
  communityUnique?: string;
  theme: TrendChartsTheme;
  /** How many weeks back to include. Default 8. */
  weeks?: number;
}

function fmtWeekTick(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function fmtDateTick(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

interface WeekChartPoint extends TrendWeek {
  week_label: string;
  fu_rate_pct: number;
}

interface OccupancyChartPoint extends TrendOccupancyPoint {
  occupancy_pct: number;
}

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  theme: TrendChartsTheme;
}> = ({ title, subtitle, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1rem 1.25rem 0.6rem',
  }}>
    <h3 style={{
      fontSize: '0.68rem',
      fontWeight: 600,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: theme.sectionTitle,
      marginBottom: subtitle ? '0.15rem' : '0.75rem',
      paddingBottom: '0.5rem',
      borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>{title}</h3>
    {subtitle && (
      <p style={{
        fontSize: '0.7rem', color: theme.textMuted, marginBottom: '0.5rem',
      }}>{subtitle}</p>
    )}
    {children}
  </div>
);

export const TrendCharts: React.FC<TrendChartsProps> = ({
  backendClientId,
  portfolio,
  communityUnique,
  theme,
  weeks = 8,
}) => {
  const [data, setData] = useState<DashboardTrends | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const options: { weeks: number; portfolio?: string; communityUnique?: string } = { weeks };
    if (portfolio) options.portfolio = portfolio;
    if (communityUnique) options.communityUnique = communityUnique;
    fetchTrends(backendClientId, options)
      .then(result => { if (!cancelled) setData(result); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [backendClientId, portfolio, communityUnique, weeks]);

  const weekPoints: WeekChartPoint[] = useMemo(() => {
    if (!data) return [];
    return data.weeks.map(w => ({
      ...w,
      week_label: fmtWeekTick(w.week_start),
      fu_rate_pct: Number((w.fu_rate * 100).toFixed(1)),
    }));
  }, [data]);

  const occupancyPoints: OccupancyChartPoint[] = useMemo(() => {
    if (!data) return [];
    return data.occupancy.map(p => ({
      ...p,
      occupancy_pct: Number((p.occupancy_rate * 100).toFixed(2)),
    }));
  }, [data]);

  if (loading && !data) {
    return <p style={{ color: theme.textMuted, padding: '1rem' }}>Loading trends…</p>;
  }
  if (error) {
    return <p style={{ color: theme.negative, padding: '1rem' }}>Trend chart error: {error}</p>;
  }
  if (!data) return null;

  const hasOccupancy = occupancyPoints.length > 1;
  const hasWeeks = weekPoints.some(w => w.leads > 0 || w.tours > 0);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
      gap: '1rem',
    }}>
      <ChartCard
        title="Leads & Tours — Weekly"
        subtitle={hasWeeks ? `Last ${weeks} weeks` : 'No data in window yet'}
        theme={theme}
      >
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weekPoints} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
              <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="week_label"
                stroke={theme.textMuted}
                tick={{ fontSize: 11 }}
                tickLine={false}
              />
              <YAxis
                stroke={theme.textMuted}
                tick={{ fontSize: 11 }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                  fontSize: 12,
                  borderRadius: 3,
                }}
              />
              <Line
                type="monotone"
                dataKey="leads"
                stroke={theme.accent}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Leads"
              />
              <Line
                type="monotone"
                dataKey="tours"
                stroke={theme.highlight}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Tours"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <ChartCard
        title="FU Rate — Weekly"
        subtitle="Financially Unqualified % of new leads. Lower is better."
        theme={theme}
      >
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weekPoints} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
              <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="week_label"
                stroke={theme.textMuted}
                tick={{ fontSize: 11 }}
                tickLine={false}
              />
              <YAxis
                stroke={theme.textMuted}
                tick={{ fontSize: 11 }}
                tickLine={false}
                unit="%"
              />
              <Tooltip
                contentStyle={{
                  background: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                  fontSize: 12,
                  borderRadius: 3,
                }}
                formatter={(value) => [`${value}%`, 'FU Rate']}
              />
              <Line
                type="monotone"
                dataKey="fu_rate_pct"
                stroke={theme.negative}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="FU Rate"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <ChartCard
        title="Occupancy %"
        subtitle={
          hasOccupancy
            ? `Daily snapshot · ${occupancyPoints.length} days`
            : 'Need at least 2 snapshots to plot a trend'
        }
        theme={theme}
      >
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={occupancyPoints} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
              <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="snapshot_date"
                stroke={theme.textMuted}
                tick={{ fontSize: 11 }}
                tickLine={false}
                tickFormatter={fmtDateTick}
              />
              <YAxis
                stroke={theme.textMuted}
                tick={{ fontSize: 11 }}
                tickLine={false}
                unit="%"
                domain={['dataMin - 2', 'dataMax + 2']}
              />
              <Tooltip
                contentStyle={{
                  background: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                  fontSize: 12,
                  borderRadius: 3,
                }}
                formatter={(value) => [`${value}%`, 'Occupancy']}
                labelFormatter={(label) => fmtDateTick(String(label))}
              />
              <Line
                type="monotone"
                dataKey="occupancy_pct"
                stroke={theme.accent}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Occupancy"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>
  );
};

export default TrendCharts;
